/**
 * Gate Pass challan — the paper that travels with the material.
 *
 * Built on the shared v5 kit in pdfTheme.js, so it prints as the same document
 * family as the Purchase Order and the GRN.
 *
 * Unlike poPdf/grnPdf this builder does NO fetching: GET /gate-pass/{id}/ already
 * returns the header, the resolved item names and the approval trail in one
 * payload, so the detail screen hands over what it is already holding. Adding a
 * fetch layer here would only re-read the document the caller just read.
 *
 * The one thing on this sheet the gate actually uses is GATE RECORD — the date and
 * time the material physically left. It prints whether or not it has happened yet:
 * before gate-out it is a blank line the security officer fills in by hand, and
 * afterwards it is the recorded timestamp.
 */
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatDate, formatDateTime, formatQty } from '../utils';
import { resolveFacilityIdentity, formatRegistrationLine } from './facilityIdentity';
import {
  GATE_PASS_DIRECTION_LABELS,
  GATE_PASS_PURPOSE_LABELS,
  GATE_PASS_PARTY_TYPE_LABELS,
  GATE_PASS_STATUS_LABELS,
} from '../constants/gatePass';
import { isReturnable, isOutward, daysOut, pendingReturnQty } from './gatePassEntity';
import {
  C,
  SIDE_M,
  U,
  CARD_W,
  CARD_X_RIGHT,
  CARD_HEAD_H,
  CARD_PAD_BOT,
  CARD_GAP,
  CONT_TOP,
  DASH,
  displayOrDash,
  drawLetterhead,
  drawTitleRow,
  measureRows,
  measureLines,
  drawCardFrame,
  drawCardTitle,
  strokeCard,
  drawRows,
  drawLines,
  tableStyles,
  drawRowRule,
  ensureSpace,
  drawContinuationHeader,
  drawSignatureRow,
  stampFooters,
} from './pdfTheme';

// Status vocabulary, stroked not filled — it stays legible on a mono printer.
const STATUS_BADGE_COLORS = {
  DRAFT:               [120, 120, 128],
  PENDING_DEPARTMENT:  [180, 120,   0],
  PENDING_STORE:       [180, 120,   0],
  APPROVED:            [ 37,  99, 235],
  GATE_OUT:            [ 15,  76,  92],
  GATE_IN:             [ 15,  76,  92],
  PARTIALLY_RETURNED:  [180, 120,   0],
  RETURNED:            [ 22, 163,  74],
  CLOSED:              [ 22, 163,  74],
  CLOSED_NOT_RETURNED: [220,  38,  38],
  REJECTED:            [220,  38,  38],
  CANCELLED:           [220,  38,  38],
};

function badgeColor(status) { return STATUS_BADGE_COLORS[status] || [120, 120, 128]; }

function badgeLabel(status) {
  return GATE_PASS_STATUS_LABELS[status] || (status || '').replace(/_/g, ' ') || DASH;
}

/**
 * formatDate/formatDateTime/formatQty answer an absent value with U+2014, which
 * jsPDF's WinAnsi encoding silently drops — the cell would print blank. Route
 * every value through these so the placeholder is always pdfTheme's DASH byte.
 */
const d = (value) => (value ? formatDate(value) : null);
const dt = (value) => (value ? formatDateTime(value) : null);
const qty = (value) => (value == null || value === '' ? DASH : displayOrDash(formatQty(value)));

function createGatePassPdfDocument(raw) {
  const gatePass = raw?.gatePass || {};
  const storeName = raw?.storeName || null;
  const facilityLogo = raw?.facilityLogo || null;
  const letterhead = raw?.letterhead ?? true;

  const lines = Array.isArray(gatePass.lines) ? gatePass.lines : [];
  const returnable = isReturnable(gatePass);
  const outward = isOutward(gatePass);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const identity = resolveFacilityIdentity();
  const registration = formatRegistrationLine(identity);
  const onNewPage = (page) =>
    drawContinuationHeader(page, identity, 'Gate Pass', gatePass.gate_pass_number, facilityLogo, letterhead);

  // ── 1. Letterhead + title ──────────────────────────────────────────────────
  let y = drawLetterhead(doc, identity, registration, facilityLogo, letterhead);
  y = drawTitleRow(doc, y, {
    title: 'GATE PASS',
    subtitle: [
      GATE_PASS_DIRECTION_LABELS[gatePass.direction] || gatePass.direction,
      returnable ? 'Returnable' : 'Non-Returnable',
      outward ? 'Material leaving hospital premises' : 'Material entering hospital premises',
    ].filter(Boolean).join('  ·  '),
    chipLabel: badgeLabel(gatePass.status),
    chipColor: badgeColor(gatePass.status),
  });

  // ── 2. GATE PASS INFORMATION + PARTY & CARRIER ─────────────────────────────
  const leftRows = [
    ['Gate Pass No', gatePass.gate_pass_number],
    ['Date & Time', dt(gatePass.gate_pass_datetime)],
    ['Direction', GATE_PASS_DIRECTION_LABELS[gatePass.direction] || gatePass.direction],
    ['Type', returnable ? 'Returnable' : 'Non-Returnable'],
    ['Purpose', GATE_PASS_PURPOSE_LABELS[gatePass.purpose] || gatePass.purpose],
    ['Store', storeName],
  ];
  if (returnable) leftRows.push(['Expected Back', d(gatePass.expected_return_date)]);

  const rightRows = [
    [outward ? 'Going To' : 'Coming From',
      GATE_PASS_PARTY_TYPE_LABELS[gatePass.party_type] || gatePass.party_type],
    ['Name', gatePass.party_name],
    ['Contact', gatePass.party_contact],
    ['Vehicle No', gatePass.vehicle_no],
    ['Carrier', gatePass.carrier_name],
    ['Carrier Contact', gatePass.carrier_contact],
  ];

  const leftM = measureRows(doc, leftRows, CARD_W);
  const rightM = measureRows(doc, rightRows, CARD_W);
  const infoH = Math.max(leftM.height, rightM.height);

  y = ensureSpace(doc, y, infoH, onNewPage);
  drawRows(doc, SIDE_M, drawCardFrame(doc, SIDE_M, y, CARD_W, infoH, 'Gate Pass Information'),
    CARD_W, leftRows, leftM.counts);
  drawRows(doc, CARD_X_RIGHT, drawCardFrame(doc, CARD_X_RIGHT, y, CARD_W, infoH, 'Party & Carrier'),
    CARD_W, rightRows, rightM.counts);
  y += infoH + CARD_GAP;

  // ── 3. PURPOSE DETAIL ──────────────────────────────────────────────────────
  // Only "Other" carries one, and when it does it IS the purpose — the word
  // "Other" on the card above says nothing on its own.
  if (gatePass.purpose_remarks) {
    const purposeM = measureLines(doc, gatePass.purpose_remarks, U);
    y = ensureSpace(doc, y, purposeM.height, onNewPage);
    drawLines(doc, SIDE_M, drawCardFrame(doc, SIDE_M, y, U, purposeM.height, 'Purpose Detail'),
      purposeM.lines, purposeM.pitch);
    y += purposeM.height + CARD_GAP;
  }

  // ── 4. GATE RECORD ─────────────────────────────────────────────────────────
  // The reason this sheet exists. Before gate-out every value is DASH, which is
  // the ruled line the officer at the barrier writes on.
  const gateRows = [
    ['Time Out', dt(gatePass.gate_out_at)],
    ['Vehicle No', gatePass.vehicle_no],
    ['Carrier', gatePass.carrier_name],
  ];
  if (returnable) {
    gateRows.push(['Days Out', gatePass.gate_out_at ? String(daysOut(gatePass) ?? DASH) : null]);
    gateRows.push(['Expected Back', d(gatePass.expected_return_date)]);
  }
  gateRows.push(['Time In / Closed', dt(gatePass.closed_at)]);

  const gateM = measureRows(doc, gateRows, U);
  y = ensureSpace(doc, y, gateM.height, onNewPage);
  drawRows(doc, SIDE_M, drawCardFrame(doc, SIDE_M, y, U, gateM.height, 'Gate Record'),
    U, gateRows, gateM.counts);
  y += gateM.height + CARD_GAP;

  // ── 5. ITEMS ───────────────────────────────────────────────────────────────
  // A returnable pass is a custody register, so it needs the returned/pending
  // columns. A non-returnable one never gets anything back; those columns would
  // print a column of zeroes.
  const columns = [
    { key: 'sl',    label: '#',                w: 6.5, halign: 'center', bold: true },
    { key: 'desc',  label: 'ITEM DESCRIPTION', w: 'auto' },
    { key: 'batch', label: returnable ? 'BATCH / SERIAL' : 'BATCH / EXP', w: 30 },
    { key: 'qty',   label: 'QTY',              w: 16,  halign: 'right', bold: true },
    ...(returnable ? [
      { key: 'ret',  label: 'RETURNED', w: 18, halign: 'right' },
      { key: 'pend', label: 'PENDING',  w: 17, halign: 'right' },
    ] : []),
    { key: 'rem',   label: 'REMARKS',          w: 38 },
  ];

  const tableRows = lines.map((line, i) => {
    // A returnable line is either a store line (batch) or an off-master asset
    // (serial number) — never both, so one cell prints whichever it has.
    const batchCell = returnable
      ? (line.batch_number || line.serial_number)
      : [line.batch_number, line.expiry_date ? `Exp: ${formatDate(line.expiry_date)}` : null]
        .filter(Boolean).join('  ');

    const cells = {
      sl:   i + 1,
      desc: displayOrDash(line.item_name || line.item_description),
      batch: displayOrDash(batchCell),
      qty:  qty(line.quantity),
      ret:  qty(line.returned_quantity ?? 0),
      pend: qty(pendingReturnQty(line)),
      rem:  displayOrDash(line.remarks),
    };
    return columns.map(col => cells[col.key]);
  });

  const emptyRow = columns.map(col => (col.key === 'desc' ? 'No line items found.' : ''));
  const TABLE_LEFT = SIDE_M + 1.5;
  const TABLE_RIGHT = SIDE_M + U - 1.5;
  let itemsCardTop = y;

  doc.autoTable({
    startY: y + CARD_HEAD_H,
    head: [columns.map(col => col.label)],
    body: tableRows.length ? tableRows : [emptyRow],
    ...tableStyles(),
    columnStyles: columns.reduce((acc, col, i) => {
      acc[i] = {
        cellWidth: col.w,
        ...(col.halign ? { halign: col.halign } : {}),
        ...(col.bold ? { fontStyle: 'bold' } : {}),
      };
      return acc;
    }, {}),
    tableWidth: U - 3,
    margin: { top: CONT_TOP + CARD_HEAD_H, left: TABLE_LEFT, right: TABLE_LEFT, bottom: 20 },
    showHead: 'everyPage',
    willDrawPage: (data) => {
      // The document's own page number, not the table-relative one — autotable can
      // push the table to a fresh page without bumping its counter.
      if (doc.internal.getCurrentPageInfo().pageNumber > 1) onNewPage(doc);
      itemsCardTop = data.cursor.y - CARD_HEAD_H;
      drawCardTitle(doc, SIDE_M, itemsCardTop, U, 'Items');
    },
    didDrawPage: (data) => {
      strokeCard(doc, SIDE_M, itemsCardTop, U, data.cursor.y + CARD_PAD_BOT - itemsCardTop);
    },
    didDrawCell: (data) => {
      if (data.column.index === 0) drawRowRule(doc, data, TABLE_LEFT, TABLE_RIGHT);
    },
  });

  y = doc.lastAutoTable.finalY + CARD_PAD_BOT + CARD_GAP;

  // ── 6. REMARKS ─────────────────────────────────────────────────────────────
  const remarksText = [
    gatePass.remarks,
    gatePass.rejection_reason ? `Rejected: ${gatePass.rejection_reason}` : null,
  ].filter(Boolean).join('\n');

  if (remarksText) {
    const remarksM = measureLines(doc, remarksText, U);
    y = ensureSpace(doc, y, remarksM.height, onNewPage);
    drawLines(doc, SIDE_M, drawCardFrame(doc, SIDE_M, y, U, remarksM.height, 'Remarks'),
      remarksM.lines, remarksM.pitch);
    y += remarksM.height + CARD_GAP;
  }

  // ── 7. APPROVAL TRAIL ──────────────────────────────────────────────────────
  // Who let this out, and when. On a printed challan that is the audit record.
  const approvals = Array.isArray(gatePass.approvals) ? gatePass.approvals : [];
  if (approvals.length) {
    const trailText = approvals
      .map(a => [
        a.stage,
        a.action,
        a.actor_name,
        a.created_at ? formatDateTime(a.created_at) : null,
        a.remarks,
      ].filter(Boolean).join('  ·  '))
      .join('\n');

    const trailM = measureLines(doc, trailText, U);
    y = ensureSpace(doc, y, trailM.height, onNewPage);
    drawLines(doc, SIDE_M, drawCardFrame(doc, SIDE_M, y, U, trailM.height, 'Approval Trail'),
      trailM.lines, trailM.pitch, C.muted);
    y += trailM.height + CARD_GAP;
  }

  // ── 8. SIGNATURE + FOOTER ──────────────────────────────────────────────────
  y = ensureSpace(doc, y, 18, onNewPage);
  drawSignatureRow(
    doc,
    y,
    `Issued by  ·  ${displayOrDash(storeName)}`,
    'Security  ·  Gate In-charge',
  );

  stampFooters(doc, identity, letterhead);
  return doc;
}

// ─── Public API ───────────────────────────────────────────────────────────────
// Only these two. poPdf.js also exports its document builder, blob and filename
// helpers, but nothing here has ever called them — a list screen that wants
// "download from the grid" can export them again at that point.

function getGatePassPdfFilename(gatePass = {}) {
  return `${gatePass.gate_pass_number || 'gate-pass'}.pdf`;
}

export function downloadGatePassPdfFromData(data) {
  createGatePassPdfDocument(data).save(getGatePassPdfFilename(data?.gatePass));
}

/** Opens the challan in a new tab, which is where the browser's own Print lives. */
export function printGatePassInNewTab(data) {
  const url = URL.createObjectURL(createGatePassPdfDocument(data).output('blob'));
  window.open(`${url}#zoom=page-fit`, '_blank', 'noopener');
}
